import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { Alert, AlertDescription } from './ui/alert';
import EmailAppPasswordHelp from './EmailAppPasswordHelp';
import {
  Mail,
  Inbox,
  Shield,
  AlertTriangle,
  CheckCircle,
  Settings,
  Monitor,
  FileText,
  Link,
  Zap,
  Eye,
  RefreshCw,
  Download,
  Upload,
  Server,
  Globe,
  Filter,
  Plus,
  X,
  XCircle,
  Lock,
  Unlock,
  Activity,
  TrendingUp,
  Users,
  Clock,
  Database,
  Search,
  BarChart3,
  PieChart,
  LineChart,
  Calendar,
  MessageSquare,
  Phone,
  Wifi,
  HardDrive,
  Cpu,
  MemoryStick,
  HelpCircle
} from 'lucide-react';

const EmailProtection = ({ user, tierInfo }) => {
  // Utility function to format timestamps to user's local timezone
  const formatLocalTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (e) {
      return timestamp;
    }
  };
  
  // Generate consistent user ID from email
  const userId = user?.email ? btoa(user.email).replace(/=/g, '') : null;
  
  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  const pollIntervalRef = useRef(null);
  const mountCountRef = useRef(0);
  
  // Increment mount counter for debugging
  mountCountRef.current++;
  
  console.log('🔍 EmailProtection mounted (#' + mountCountRef.current + '):', { 
    hasUser: !!user, 
    email: user?.email, 
    userId: userId,
    userObject: user 
  });
  
  const [emailStats, setEmailStats] = useState({
    threatsBlocked: 0,
    emailsScanned: 0,
    attachmentsChecked: 0,
    phishingDetected: 0,
    spamBlocked: 0,
    malwareQuarantined: 0,
    spamBlockedToday: 0,
    suspiciousEmails: 0,
    whitelistedSenders: 0
  });

  const [quarantineItems, setQuarantineItems] = useState([]);
  const [loadingQuarantine, setLoadingQuarantine] = useState(false);

  const [emailConnection, setEmailConnection] = useState({
    status: 'disconnected', // 'disconnected', 'connecting', 'connected', 'error'
    provider: '',
    email: '',
    lastSync: null,
    error: null
  });

  const [connectionModal, setConnectionModal] = useState({
    isOpen: false,
    selectedProvider: null,
    credentials: {
      email: '',
      password: ''
    }
  });

  const [showHelpPanel, setShowHelpPanel] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [realtimeAlerts, setRealtimeAlerts] = useState([]);
  const [monitoringActive, setMonitoringActive] = useState(false);
  const [showSetupGuide, setShowSetupGuide] = useState(null); // null or provider name
  
  // Settings state
  const [protectionSettings, setProtectionSettings] = useState({
    realtimeScanning: true,
    attachmentAnalysis: true,
    phishingDetection: true,
    spamFiltering: true,
    contentAnalysis: true,
    advancedHeuristics: true
  });
  
  const [notificationSettings, setNotificationSettings] = useState({
    emailAlerts: true,
    smsNotifications: false,
    desktopNotifications: true,
    weeklyReports: true,
    emergencyAlerts: true
  });
  
  const [contactInfo, setContactInfo] = useState({
    email_address: '',
    phone_number: ''
  });
  
  const [whitelistItems, setWhitelistItems] = useState([]);
  const [loadingWhitelist, setLoadingWhitelist] = useState(false);
  
  const [spamFilterSettings, setSpamFilterSettings] = useState({
    aggressiveFiltering: true,
    bayesianLearning: true,
    senderReputation: true,
    contentAnalysis: true,
    languageDetection: true,
    attachmentScanning: true
  });

  // Memoized function to fetch quarantine items
  const fetchQuarantineItems = useCallback(async () => {
    if (!userId || emailConnection.status !== 'connected' || !isMountedRef.current) {
      return;
    }
    
    setLoadingQuarantine(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://cyberguard-backend-wa00.onrender.com';
      console.log('📥 Fetching quarantine items for userId:', userId);
      const response = await fetch(`${API_URL}/api/email/quarantine/${userId}?limit=50`);
      const data = await response.json();
      
      if (data.success && isMountedRef.current) {
        setQuarantineItems(data.items);
        console.log(`✅ Loaded ${data.items.length} quarantine items`);
      }
    } catch (error) {
      console.error('Error fetching quarantine items:', error);
    } finally {
      if (isMountedRef.current) {
        setLoadingQuarantine(false);
      }
    }
  }, [userId, emailConnection.status]);

  // Cleanup polling interval helper
  const clearPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      console.log('🧹 Clearing polling interval');
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Poll monitoring status every 30 seconds
  const startStatusPolling = useCallback((userIdParam) => {
    // Clear any existing polling first
    clearPolling();
    
    const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://cyberguard-backend-wa00.onrender.com';
    
    console.log('⏱️ Setting up status polling for userId:', userIdParam);
    const statusUrl = `${API_URL}/api/email/monitor/status/${userIdParam}`;
    console.log('📡 Status polling URL:', statusUrl);
    
    pollIntervalRef.current = setInterval(async () => {
      if (!isMountedRef.current) {
        clearPolling();
        return;
      }
      
      try {
        console.log('🔄 Polling status for userId:', userIdParam);
        const response = await fetch(statusUrl, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        const statusData = await response.json();
        console.log('📊 Received monitoring status:', statusData);
        
        if (!isMountedRef.current) return;
        
        if (statusData.status === 'active' || statusData.status === 'monitoring') {
          // Update stats from real monitoring data
          const threatsDetected = statusData.threats_detected || 0;
          const todayStart = new Date();
          todayStart.setHours(0, 0, 0, 0);
          
          setEmailStats(prev => ({
            ...prev,
            emailsScanned: statusData.emails_processed || prev.emailsScanned,
            threatsBlocked: threatsDetected,
            attachmentsChecked: statusData.attachments_checked || 0,
            phishingDetected: Math.floor(threatsDetected * 0.4),
            spamBlocked: Math.floor(threatsDetected * 0.3),
            malwareQuarantined: Math.floor(threatsDetected * 0.3),
            // Use backend-calculated today's spam count
            spamBlockedToday: statusData.spam_blocked_today || 0,
            // Use backend-calculated suspicious emails count
            suspiciousEmails: statusData.suspicious_emails || 0,
            // Count whitelisted senders
            whitelistedSenders: whitelistItems.length
          }));
          
          // Update last sync time
          if (statusData.last_check) {
            setEmailConnection(prev => ({
              ...prev,
              lastSync: new Date(statusData.last_check)
            }));
          }
          
          // Fetch quarantine items if threats detected
          if (statusData.threats_detected > 0) {
            fetchQuarantineItems();
          }
          
          console.log(`📊 Monitoring status: ${statusData.emails_processed} emails processed, ${statusData.threats_detected} threats detected`);
        } else if (statusData.status === 'inactive') {
          // Monitoring stopped
          clearPolling();
          if (isMountedRef.current) {
            setMonitoringActive(false);
          }
        }
      } catch (error) {
        console.error('Error polling monitoring status:', error);
      }
    }, 30000); // Poll every 30 seconds
  }, [clearPolling, fetchQuarantineItems]);

  // Restore connection state from localStorage on mount
  useEffect(() => {
    // Reset mounted state in case of StrictMode double-mount
    isMountedRef.current = true;
    
    const restoreConnection = async () => {
      console.log('🔄 Restore effect running on mount...');
      
      const savedConnection = localStorage.getItem('emailConnection');
      console.log('📦 Saved connection from localStorage:', savedConnection);
      
      if (!savedConnection) {
        console.log('ℹ️ No saved connection found');
        return;
      }
      
      try {
        const connectionData = JSON.parse(savedConnection);
        console.log('📧 Attempting to restore connection:', connectionData);
        
        // Use the saved userId from localStorage (not the one from user object)
        const savedUserId = connectionData.userId;
        
        if (!savedUserId) {
          console.log('⚠️ No userId in saved connection, clearing...');
          localStorage.removeItem('emailConnection');
          return;
        }
        
        console.log('👤 Using saved userId for restore:', savedUserId);
        
        // Verify the connection is still active on the backend
        const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://cyberguard-backend-wa00.onrender.com';
        console.log('🌐 API URL:', API_URL);
        
        const statusUrl = `${API_URL}/api/email/monitor/status/${savedUserId}`;
        console.log('📡 Checking status at:', statusUrl);
        
        const response = await fetch(statusUrl);
        const statusData = await response.json();
        
        console.log('📊 Status response:', statusData);
        console.log('🔍 Status check:', {
          hasStatus: !!statusData.status,
          statusValue: statusData.status,
          isActive: statusData.status === 'active',
          isMonitoring: statusData.status === 'monitoring',
          willRestore: statusData.status === 'active' || statusData.status === 'monitoring'
        });
        
        if (statusData.status === 'active' || statusData.status === 'monitoring') {
          console.log('✅ Restored active email monitoring session');
          setEmailConnection({
            status: 'connected',
            provider: connectionData.provider,
            email: connectionData.email,
            lastSync: connectionData.lastSync ? new Date(connectionData.lastSync) : null,
            error: null
          });
          setMonitoringActive(true);
          
          // Restore stats
          console.log('📊 Setting stats from status data:', {
            emails_processed: statusData.emails_processed,
            threats_detected: statusData.threats_detected
          });
          setEmailStats(prev => ({
            ...prev,
            emailsScanned: statusData.emails_processed || 0,
            threatsBlocked: statusData.threats_detected || 0,
            phishingDetected: Math.floor((statusData.threats_detected || 0) * 0.4),
            spamBlocked: Math.floor((statusData.threats_detected || 0) * 0.3),
            malwareQuarantined: Math.floor((statusData.threats_detected || 0) * 0.3)
          }));
          console.log('✅ Stats updated successfully');
          
          // Fetch quarantine items if threats detected
          if (statusData.threats_detected > 0) {
            console.log('📥 Fetching quarantine items on restore...');
            try {
              const quarantineResponse = await fetch(`${API_URL}/api/email/quarantine/${savedUserId}?limit=50`);
              const quarantineData = await quarantineResponse.json();
              
              if (quarantineData.success) {
                setQuarantineItems(quarantineData.items);
                console.log(`✅ Loaded ${quarantineData.items.length} quarantine items on restore`);
              }
            } catch (error) {
              console.error('Error fetching quarantine items on restore:', error);
            }
          }
          
          // Start polling with the saved userId
          startStatusPolling(savedUserId);
        } else if (statusData.status === 'inactive') {
          // Backend monitoring session expired - show reconnect prompt
          console.log('⚠️ Backend monitoring session expired - prompting user to reconnect');
          
          setEmailConnection({
            status: 'disconnected',
            provider: connectionData.provider,
            email: connectionData.email,
            lastSync: connectionData.lastSync ? new Date(connectionData.lastSync) : null,
            error: `Your ${connectionData.provider} monitoring session expired. Click the button below to reconnect.`
          });
          
          // Keep the connection data so user can easily reconnect
          // Don't clear localStorage - we'll use it to pre-fill the reconnect form
        } else {
          console.log('❌ Unknown status from backend, clearing saved data');
          localStorage.removeItem('emailConnection');
        }
      } catch (error) {
        console.error('❌ Error restoring connection:', error);
        localStorage.removeItem('emailConnection');
      }
    };
    
    restoreConnection();
    
    // Cleanup on unmount
    return () => {
      console.log('🧹 EmailProtection unmounting - cleaning up');
      isMountedRef.current = false;
      clearPolling();
    };
  }, []); // Run once on mount

  // Real-time monitoring updates when connected
  useEffect(() => {
    if (monitoringActive && emailConnection.status === 'connected') {
      console.log('📊 Real-time email monitoring active');
      // Monitoring updates happen via startStatusPolling()
      // No fake data generation - all data comes from backend
    }
  }, [monitoringActive, emailConnection.status]);

  // Email provider configurations
  const emailProviders = [
    { 
      name: 'Gmail', 
      value: 'gmail', 
      icon: Mail, 
      iconColor: 'text-red-500', 
      bgColor: 'bg-red-50 border-red-200', 
      port: 993, 
      secure: true,
      description: 'Connect your Google Gmail account',
      setupSteps: ['Enable 2-factor authentication', 'Generate app password', 'Connect securely']
    },
    { 
      name: 'Outlook', 
      value: 'outlook', 
      icon: Inbox, 
      iconColor: 'text-blue-500', 
      bgColor: 'bg-blue-50 border-blue-200', 
      port: 993, 
      secure: true,
      description: 'Connect your Microsoft Outlook account',
      setupSteps: ['Use your Microsoft credentials', 'Enable OAuth authentication', 'Start monitoring']
    },
    { 
      name: 'Yahoo Mail', 
      value: 'yahoo', 
      icon: MessageSquare, 
      iconColor: 'text-purple-500', 
      bgColor: 'bg-purple-50 border-purple-200', 
      port: 993, 
      secure: true,
      description: 'Connect your Yahoo Mail account',
      setupSteps: ['Generate app password', 'Enable IMAP access', 'Secure connection']
    },
    { 
      name: 'Custom IMAP', 
      value: 'custom', 
      icon: Server, 
      iconColor: 'text-gray-500', 
      bgColor: 'bg-gray-50 border-gray-200', 
      port: 993, 
      secure: true,
      description: 'Connect any IMAP-compatible email service',
      setupSteps: ['Enter IMAP settings', 'Configure authentication', 'Test connection']
    }
  ];

  const debugMonitoring = async () => {
    if (!userId) {
      console.log('❌ No user ID available');
      return;
    }
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://cyberguard-backend-wa00.onrender.com';
      console.log('🔍 Fetching debug info for userId:', userId);
      const response = await fetch(`${API_URL}/api/email/monitor/debug/${userId}`);
      const data = await response.json();
      
      console.log('🔍 DEBUG INFO:', data);
      alert(`Debug Info:\n${JSON.stringify(data, null, 2)}`);
    } catch (error) {
      console.error('Error fetching debug info:', error);
    }
  };

  const handleQuarantineAction = async (item, action) => {
    if (!isMountedRef.current) return;
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://cyberguard-backend-wa00.onrender.com';
      console.log(`🎯 Performing ${action} action for userId:`, userId);
      const response = await fetch(`${API_URL}/api/email/quarantine/${userId}/action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          timestamp: item.timestamp,
          action: action  // 'released' or 'deleted'
        })
      });
      
      const data = await response.json();
      
      if (data.success && isMountedRef.current) {
        // Remove from local state
        setQuarantineItems(prev => prev.filter(i => i.timestamp !== item.timestamp));
        console.log(`✅ Item ${action}`);
      }
    } catch (error) {
      console.error(`Error ${action} item:`, error);
    }
  };
  
  const handleTrustSender = async (item) => {
    if (!isMountedRef.current || !item.email_from) return;
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://cyberguard-backend-wa00.onrender.com';
      console.log(`🤝 Adding ${item.email_from} to whitelist for userId:`, userId);
      
      // Add to whitelist
      const response = await fetch(`${API_URL}/api/email/whitelist/${userId}/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: item.email_from,
          reason: `Trusted sender - released from quarantine`
        })
      });
      
      const data = await response.json();
      
      if (data.success && isMountedRef.current) {
        console.log(`✅ ${item.email_from} added to whitelist`);
        
        // Also release from quarantine
        await handleQuarantineAction(item, 'released');
        
        // Refresh whitelist
        fetchWhitelist();
      } else {
        console.error('Failed to add to whitelist:', data);
      }
    } catch (error) {
      console.error('Error trusting sender:', error);
    }
  };
  
  const fetchWhitelist = useCallback(async () => {
    if (!userId || !isMountedRef.current) return;
    
    setLoadingWhitelist(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://cyberguard-backend-wa00.onrender.com';
      console.log('📥 Fetching whitelist for userId:', userId);
      const response = await fetch(`${API_URL}/api/email/whitelist/${userId}`);
      const data = await response.json();
      
      if (isMountedRef.current) {
        setWhitelistItems(Array.isArray(data) ? data : []);
        console.log(`✅ Loaded ${data.length} whitelist items`);
      }
    } catch (error) {
      console.error('Error fetching whitelist:', error);
    } finally {
      if (isMountedRef.current) {
        setLoadingWhitelist(false);
      }
    }
  }, [userId]);
  
  const handleRemoveFromWhitelist = async (email) => {
    if (!isMountedRef.current) return;
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://cyberguard-backend-wa00.onrender.com';
      console.log(`🗑️ Removing ${email} from whitelist for userId:`, userId);
      
      const response = await fetch(`${API_URL}/api/email/whitelist/${userId}/remove`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (data.success && isMountedRef.current) {
        console.log(`✅ ${email} removed from whitelist`);
        fetchWhitelist();
      }
    } catch (error) {
      console.error('Error removing from whitelist:', error);
    }
  };
  
  const saveNotificationSettings = async () => {
    if (!isMountedRef.current) return;
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://cyberguard-backend-wa00.onrender.com';
      console.log('💾 Saving notification settings for userId:', userId);
      
      // Convert camelCase to snake_case for backend
      const settings = {
        email_alerts: notificationSettings.emailAlerts,
        sms_notifications: notificationSettings.smsNotifications,
        desktop_notifications: notificationSettings.desktopNotifications,
        weekly_reports: notificationSettings.weeklyReports,
        emergency_alerts: notificationSettings.emergencyAlerts,
        email_address: contactInfo.email_address,
        phone_number: contactInfo.phone_number
      };
      
      const response = await fetch(`${API_URL}/api/notifications/settings/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      const data = await response.json();
      
      if (data.success) {
        console.log('✅ Notification settings saved');
        alert('Notification settings saved successfully!');
      } else {
        console.error('Failed to save settings:', data);
        alert('Failed to save settings. Please try again.');
      }
    } catch (error) {
      console.error('Error saving notification settings:', error);
      alert('Error saving settings. Please try again.');
    }
  };
  
  const sendTestNotification = async (type = 'email') => {
    if (!isMountedRef.current) return;
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://cyberguard-backend-wa00.onrender.com';
      console.log(`📧 Sending test ${type} notification for userId:`, userId);
      
      const response = await fetch(`${API_URL}/api/notifications/test/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ type })
      });
      
      const data = await response.json();
      
      if (data.success) {
        console.log('✅ Test notification sent');
        alert(data.message || 'Test notification sent successfully!');
      } else {
        console.error('Failed to send test:', data);
        alert(data.error || 'Failed to send test notification. Please check your contact information.');
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      alert('Error sending test notification. Please try again.');
    }
  };

  const handleConnectEmail = async (provider) => {
    // Open credential modal instead of connecting immediately
    setConnectionModal({
      isOpen: true,
      selectedProvider: provider,
      credentials: {
        email: '', // Leave blank - user should enter their personal email, not dashboard login
        password: ''
      }
    });
  };

  const handleSubmitCredentials = async () => {
    const provider = connectionModal.selectedProvider;
    
    // Validate inputs
    if (!connectionModal.credentials.email || !connectionModal.credentials.password) {
      setEmailConnection({
        status: 'error',
        provider: provider.name,
        email: '',
        lastSync: null,
        error: 'Please enter both email and password'
      });
      setConnectionModal({ ...connectionModal, isOpen: false });
      return;
    }

    // Close modal and start connection
    setConnectionModal({ ...connectionModal, isOpen: false });
    setEmailConnection(prev => ({ ...prev, status: 'connecting', provider: provider.name }));
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://cyberguard-backend-wa00.onrender.com';
      
      console.log('� ========== STARTING EMAIL MONITORING ==========');
      console.log('📧 User object:', user);
      console.log('🆔 Generated userId:', userId);
      console.log('📨 Provider:', provider.value);
      console.log('📮 Email:', connectionModal.credentials.email);
      console.log('🌐 API URL:', API_URL);
      
      if (!userId) {
        console.error('❌ CRITICAL: No userId available!');
        throw new Error('User ID is required for email monitoring');
      }
      
      // Call real backend API to start monitoring
      const requestBody = {
        user_id: userId,
        provider: provider.value,
        email: connectionModal.credentials.email,
        credentials: {
          email: connectionModal.credentials.email,
          password: connectionModal.credentials.password
        }
      };
      
      console.log('📤 Sending request to backend:', {
        url: `${API_URL}/api/email/monitor/start`,
        body: { ...requestBody, credentials: { email: requestBody.credentials.email, password: '***' } }
      });
      
      const response = await fetch(`${API_URL}/api/email/monitor/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      console.log('📨 Backend response:', data);
      console.log('🔄 Response user_id:', data.user_id);
      console.log('🔄 Original userId:', userId);
      
      if (data.status === 'active') {
        // Connection successful
        console.log('✅ Email monitoring started successfully with user_id:', userId);
        
        const connectionState = {
          status: 'connected',
          provider: provider.name,
          email: connectionModal.credentials.email,
          lastSync: new Date(),
          error: null
        };
        
        setEmailConnection(connectionState);
        setMonitoringActive(true);
        
        // Save connection state to localStorage (without password)
        const savedConnectionData = {
          provider: provider.name,
          email: connectionModal.credentials.email,
          userId: userId,
          lastSync: new Date().toISOString()
        };
        localStorage.setItem('emailConnection', JSON.stringify(savedConnectionData));
        console.log('💾 Saved connection to localStorage:', savedConnectionData);
        
        // Start polling for monitoring status
        startStatusPolling(userId || `user_${Date.now()}`);
      } else {
        // Connection failed
        throw new Error(data.error || 'Failed to start email monitoring');
      }
      
      // Clear credentials from memory
      setConnectionModal({
        isOpen: false,
        selectedProvider: null,
        credentials: { email: '', password: '' }
      });
      
    } catch (error) {
      // Handle connection errors
      console.error('❌ Email connection failed:', error.message);
      setEmailConnection({
        status: 'error',
        provider: provider.name,
        email: '',
        lastSync: null,
        error: error.message || 'Connection failed. Please check your credentials and try again.'
      });
      // Clear credentials
      setConnectionModal({
        isOpen: false,
        selectedProvider: null,
        credentials: { email: '', password: '' }
      });
    }
  };
  
  const handleDisconnect = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://cyberguard-backend-wa00.onrender.com';
      
      console.log('🔴 Stopping email monitoring...');
      
      // Stop backend monitoring
      await fetch(`${API_URL}/api/email/monitor/stop`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          user_id: userId || `user_${Date.now()}`
        })
      });
      
      // Clear polling interval using ref
      clearPolling();
      
      // Clear saved connection from localStorage
      localStorage.removeItem('emailConnection');
      
      if (isMountedRef.current) {
        setEmailConnection({
          status: 'disconnected',
          provider: '',
          email: '',
          lastSync: null,
          error: null
        });
        setMonitoringActive(false);
      }
      
      console.log('✅ Email monitoring stopped');
    } catch (error) {
      console.error('Error disconnecting email monitoring:', error);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-400 bg-red-500/20 border-red-500/50';
      case 'high': return 'text-orange-400 bg-orange-500/20 border-orange-500/50';
      case 'medium': return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/50';
      case 'low': return 'text-blue-400 bg-blue-500/20 border-blue-500/50';
      default: return 'text-gray-400 bg-gray-500/20 border-gray-500/50';
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'threat': return <Shield className="h-4 w-4 text-red-400" />;
      case 'phishing': return <AlertTriangle className="h-4 w-4 text-orange-400" />;
      case 'spam': return <Filter className="h-4 w-4 text-blue-400" />;
      case 'policy': return <Eye className="h-4 w-4 text-purple-400" />;
      default: return <Mail className="h-4 w-4 text-gray-400" />;
    }
  };

  const clearEmailCache = () => {
    localStorage.removeItem('emailConnection');
    setEmailConnection({
      status: 'disconnected',
      provider: '',
      email: '',
      lastSync: null,
      error: null
    });
    setMonitoringActive(false);
    clearPolling();
    console.log('🧹 Cleared all email connection cache');
    alert('Email connection cache cleared! You can now connect with a fresh email address.');
  };
  
  // Load whitelist when component mounts or settings tab is accessed
  useEffect(() => {
    if (emailConnection.status === 'connected' && userId) {
      fetchWhitelist();
    }
  }, [emailConnection.status, userId, fetchWhitelist]);

  return (
    <div className="space-y-6">
      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      
      {/* Real-Time Monitoring Badge */}
      <Alert className={
        emailConnection.status === 'connected' ? 'bg-green-900/20 border-green-500/50' : 
        emailConnection.error ? 'bg-orange-900/20 border-orange-500/50' : 
        'bg-blue-900/20 border-blue-500/50'
      }>
        <AlertDescription className={
          emailConnection.status === 'connected' ? 'text-green-200' : 
          emailConnection.error ? 'text-orange-200' : 
          'text-blue-200'
        }>
          {emailConnection.status === 'connected' ? (
            <>
              <strong>🟢 Live Protection Active:</strong> Your email is being monitored in real-time. All threats are automatically detected and blocked.
            </>
          ) : emailConnection.error ? (
            <div className="flex items-center justify-between">
              <div>
                <strong>⚠️ Session Expired:</strong> {emailConnection.error}
              </div>
              <Button
                onClick={() => {
                  // Get saved connection info
                  const saved = JSON.parse(localStorage.getItem('emailConnection') || '{}');
                  const provider = emailProviders.find(p => p.name === saved.provider);
                  if (provider) {
                    setConnectionModal({
                      isOpen: true,
                      selectedProvider: provider,
                      credentials: {
                        email: saved.email || '',
                        password: ''
                      }
                    });
                  }
                }}
                size="sm"
                style={{
                  background: 'rgba(255, 107, 53, 0.2)',
                  border: '1px solid rgba(255, 107, 53, 0.5)',
                  color: '#ff6b35'
                }}
              >
                Reconnect {emailConnection.provider}
              </Button>
            </div>
          ) : (
            <>
              <strong>📧 Email Protection Ready:</strong> Connect your email provider below to enable real-time threat detection and automatic protection.
            </>
          )}
        </AlertDescription>
      </Alert>
      
      {/* Setup Guide Modal */}
      {showSetupGuide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" style={{
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(10px)'
          }}>
          <div className="max-w-3xl w-full rounded-2xl my-8" style={{
              background: 'rgba(6, 9, 15, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(0, 212, 255, 0.3)',
              boxShadow: '0 20px 60px rgba(0, 212, 255, 0.3)'
            }}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold" style={{ color: '#ffffff' }}>Setup Guide: {showSetupGuide}</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSetupGuide(null)}
                  style={{ color: '#94a3b8' }}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="space-y-6">
                {showSetupGuide === 'Gmail' && (
                  <>
                    <Alert className="bg-blue-900/20 border-blue-500/50">
                      <AlertDescription className="text-blue-200">
                        <strong>📝 Note:</strong> Gmail requires an App Password for third-party applications. Your regular Gmail password will NOT work.
                      </AlertDescription>
                    </Alert>

                    <div className="space-y-4">
                      <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <h4 className="font-semibold mb-3 flex items-center" style={{ color: '#00ff88' }}>
                          <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm mr-2">1</span>
                          Enable 2-Factor Authentication
                        </h4>
                        <ol className="list-disc list-inside space-y-2 text-sm" style={{ color: '#e2e8f0' }}>
                          <li>Go to <a href="https://myaccount.google.com/security" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">myaccount.google.com/security</a></li>
                          <li>Under "Signing in to Google", click on "2-Step Verification"</li>
                          <li>Follow the prompts to turn on 2-Step Verification</li>
                          <li>You'll need your phone to receive verification codes</li>
                        </ol>
                      </div>

                      <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <h4 className="font-semibold mb-3 flex items-center" style={{ color: '#00ff88' }}>
                          <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm mr-2">2</span>
                          Generate App Password
                        </h4>
                        <ol className="list-disc list-inside space-y-2 text-sm" style={{ color: '#e2e8f0' }}>
                          <li>Go to <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">myaccount.google.com/apppasswords</a></li>
                          <li>Select "Mail" as the app</li>
                          <li>Select "Other (Custom name)" as the device</li>
                          <li>Enter "CyberGuard Email Protection" as the name</li>
                          <li>Click "Generate"</li>
                          <li>Copy the 16-character password (spaces don't matter)</li>
                        </ol>
                      </div>

                      <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <h4 className="font-semibold mb-3 flex items-center" style={{ color: '#00ff88' }}>
                          <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm mr-2">3</span>
                          Connect to CyberGuard
                        </h4>
                        <ol className="list-disc list-inside space-y-2 text-sm" style={{ color: '#e2e8f0' }}>
                          <li>Enter your full Gmail address (example@gmail.com)</li>
                          <li>Paste the 16-character App Password</li>
                          <li>Click "Connect Securely"</li>
                          <li>Your email will be monitored in real-time</li>
                        </ol>
                      </div>
                    </div>

                    <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 107, 53, 0.1)', border: '1px solid rgba(255, 107, 53, 0.3)' }}>
                      <p className="text-sm" style={{ color: '#ff6b35' }}>
                        <strong>⚠️ Important:</strong> Never share your App Password. We don't store it - it's only used to establish the connection.
                      </p>
                    </div>
                  </>
                )}

                {showSetupGuide === 'Outlook' && (
                  <>
                    <Alert className="bg-blue-900/20 border-blue-500/50">
                      <AlertDescription className="text-blue-200">
                        <strong>📝 Note:</strong> Microsoft requires an App Password for email clients. Your regular Microsoft password will NOT work.
                      </AlertDescription>
                    </Alert>

                    <div className="space-y-4">
                      <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <h4 className="font-semibold mb-3 flex items-center" style={{ color: '#00ff88' }}>
                          <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm mr-2">1</span>
                          Enable 2-Step Verification
                        </h4>
                        <ol className="list-disc list-inside space-y-2 text-sm" style={{ color: '#e2e8f0' }}>
                          <li>Go to <a href="https://account.microsoft.com/security" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">account.microsoft.com/security</a></li>
                          <li>Click on "Advanced security options"</li>
                          <li>Under "Two-step verification", click "Turn on"</li>
                          <li>Follow the setup process to add a verification method</li>
                        </ol>
                      </div>

                      <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <h4 className="font-semibold mb-3 flex items-center" style={{ color: '#00ff88' }}>
                          <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm mr-2">2</span>
                          Create App Password
                        </h4>
                        <ol className="list-disc list-inside space-y-2 text-sm" style={{ color: '#e2e8f0' }}>
                          <li>Stay in "Advanced security options"</li>
                          <li>Scroll down to "App passwords"</li>
                          <li>Click "Create a new app password"</li>
                          <li>Copy the generated password immediately</li>
                          <li>You won't be able to see it again</li>
                        </ol>
                      </div>

                      <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <h4 className="font-semibold mb-3 flex items-center" style={{ color: '#00ff88' }}>
                          <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm mr-2">3</span>
                          Connect to CyberGuard
                        </h4>
                        <ol className="list-disc list-inside space-y-2 text-sm" style={{ color: '#e2e8f0' }}>
                          <li>Enter your full Outlook email address</li>
                          <li>Paste the App Password you just created</li>
                          <li>Click "Connect Securely"</li>
                          <li>Real-time protection will activate immediately</li>
                        </ol>
                      </div>
                    </div>
                  </>
                )}

                {showSetupGuide === 'Yahoo Mail' && (
                  <>
                    <Alert className="bg-blue-900/20 border-blue-500/50">
                      <AlertDescription className="text-blue-200">
                        <strong>📝 Note:</strong> Yahoo requires an App Password for third-party applications.
                      </AlertDescription>
                    </Alert>

                    <div className="space-y-4">
                      <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <h4 className="font-semibold mb-3 flex items-center" style={{ color: '#00ff88' }}>
                          <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm mr-2">1</span>
                          Generate App Password
                        </h4>
                        <ol className="list-disc list-inside space-y-2 text-sm" style={{ color: '#e2e8f0' }}>
                          <li>Go to <a href="https://login.yahoo.com/account/security" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">login.yahoo.com/account/security</a></li>
                          <li>Click on "Generate app password"</li>
                          <li>Select "Other App" from the dropdown</li>
                          <li>Enter "CyberGuard" as the app name</li>
                          <li>Click "Generate"</li>
                          <li>Copy the password immediately (shown once)</li>
                        </ol>
                      </div>

                      <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <h4 className="font-semibold mb-3 flex items-center" style={{ color: '#00ff88' }}>
                          <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm mr-2">2</span>
                          Connect to CyberGuard
                        </h4>
                        <ol className="list-disc list-inside space-y-2 text-sm" style={{ color: '#e2e8f0' }}>
                          <li>Enter your full Yahoo email address</li>
                          <li>Paste the App Password</li>
                          <li>Click "Connect Securely"</li>
                          <li>Your Yahoo mail is now protected</li>
                        </ol>
                      </div>
                    </div>
                  </>
                )}

                {showSetupGuide === 'Custom IMAP' && (
                  <>
                    <Alert className="bg-blue-900/20 border-blue-500/50">
                      <AlertDescription className="text-blue-200">
                        <strong>📝 Note:</strong> Custom IMAP requires your email provider to support IMAP access.
                      </AlertDescription>
                    </Alert>

                    <div className="space-y-4">
                      <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <h4 className="font-semibold mb-3 flex items-center" style={{ color: '#00ff88' }}>
                          <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm mr-2">1</span>
                          Check IMAP Settings
                        </h4>
                        <ol className="list-disc list-inside space-y-2 text-sm" style={{ color: '#e2e8f0' }}>
                          <li>Contact your email provider for IMAP server details</li>
                          <li>You'll need: Server address, Port (usually 993), SSL/TLS settings</li>
                          <li>Enable IMAP access in your email account settings</li>
                        </ol>
                      </div>

                      <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <h4 className="font-semibold mb-3 flex items-center" style={{ color: '#00ff88' }}>
                          <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm mr-2">2</span>
                          Connect to CyberGuard
                        </h4>
                        <ol className="list-disc list-inside space-y-2 text-sm" style={{ color: '#e2e8f0' }}>
                          <li>Enter your full email address</li>
                          <li>Enter your email password or app password</li>
                          <li>We'll automatically try common IMAP settings</li>
                          <li>If connection fails, contact support for custom configuration</li>
                        </ol>
                      </div>
                    </div>
                  </>
                )}

                <div className="flex space-x-3">
                  <Button
                    onClick={() => {
                      setShowSetupGuide(null);
                      // Find and click the provider to open connection modal
                      const provider = emailProviders.find(p => p.name === showSetupGuide);
                      if (provider) {
                        handleConnectEmail(provider);
                      }
                    }}
                    className="flex-1"
                    style={{
                      background: 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)',
                      color: '#ffffff',
                      border: 'none'
                    }}
                  >
                    <Lock className="h-4 w-4 mr-2" />
                    I'm Ready to Connect
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowSetupGuide(null)}
                    style={{
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#e2e8f0',
                      background: 'rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    Close Guide
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Credential Input Modal */}
      {connectionModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(10px)'
          }}>
          <div className="max-w-md w-full rounded-2xl transition-all duration-300" style={{
              background: 'rgba(6, 9, 15, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(0, 212, 255, 0.3)',
              boxShadow: '0 20px 60px rgba(0, 212, 255, 0.3)'
            }}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold" style={{ color: '#ffffff' }}>Connect to {connectionModal.selectedProvider?.name}</h3>
                  <p className="text-sm" style={{ color: '#94a3b8' }}>Enter your email credentials</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConnectionModal({ ...connectionModal, isOpen: false })}
                  style={{ color: '#94a3b8' }}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#e2e8f0' }}>
                    Email Address
                  </label>
                  <Input
                    type="email"
                    placeholder="your.email@example.com"
                    value={connectionModal.credentials.email}
                    onChange={(e) => setConnectionModal({
                      ...connectionModal,
                      credentials: { ...connectionModal.credentials, email: e.target.value }
                    })}
                    className="w-full"
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      color: '#ffffff'
                    }}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium" style={{ color: '#e2e8f0' }}>
                      Password / App Password
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowHelpPanel(true)}
                      className="flex items-center space-x-1 text-xs font-medium transition-all hover:scale-105"
                      style={{ color: '#00d4ff' }}
                    >
                      <HelpCircle className="h-4 w-4" />
                      <span>Need help?</span>
                    </button>
                  </div>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••••••"
                      value={connectionModal.credentials.password}
                      onChange={(e) => setConnectionModal({
                        ...connectionModal,
                        credentials: { ...connectionModal.credentials, password: e.target.value }
                      })}
                      className="w-full pr-10"
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        color: '#ffffff'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    >
                      {showPassword ? <Eye className="h-5 w-5" /> : <Eye className="h-5 w-5" style={{ opacity: 0.5 }} />}
                    </button>
                  </div>
                  <p className="text-xs mt-1" style={{ color: '#94a3b8' }}>
                    For Gmail/Outlook, use an app-specific password
                  </p>
                </div>

                <div className="p-3 rounded-lg" style={{
                    background: 'rgba(0, 212, 255, 0.1)',
                    border: '1px solid rgba(0, 212, 255, 0.2)'
                  }}>
                  <div className="flex items-start space-x-2">
                    <Lock className="h-4 w-4 mt-0.5" style={{ color: '#00d4ff' }} />
                    <p className="text-xs" style={{ color: '#e2e8f0' }}>
                      Your credentials are encrypted and never stored on our servers. We only use them to establish a secure connection.
                    </p>
                  </div>
                </div>

                <div className="flex space-x-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setConnectionModal({ ...connectionModal, isOpen: false })}
                    className="flex-1"
                    style={{
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#e2e8f0',
                      background: 'rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmitCredentials}
                    className="flex-1"
                    style={{
                      background: 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)',
                      color: '#ffffff',
                      border: 'none'
                    }}
                  >
                    <Lock className="h-4 w-4 mr-2" />
                    Connect Securely
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Email Connection Status */}
      <Card className="transition-all duration-300" style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          borderRadius: '1.5rem',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}>
        <CardHeader>
          <CardTitle className="flex items-center" style={{ color: '#ffffff' }}>
            <Mail className="h-5 w-5 mr-2" style={{ color: '#00ff88' }} />
            Email Protection Hub
            <Badge className={`ml-3 border-0 ${emailConnection.status === 'connected' ? '' : ''}`} style={{
                background: emailConnection.status === 'connected' ? 'rgba(0, 255, 136, 0.2)' : 'rgba(255, 107, 53, 0.2)',
                color: emailConnection.status === 'connected' ? '#00ff88' : '#ff6b35'
              }}>
              {emailConnection.status === 'connected' ? (
                <div className="flex items-center">
                  <div className="w-2 h-2 rounded-full mr-2 animate-pulse" style={{ background: '#00ff88' }}></div>
                  Connected
                </div>
              ) : (
                <div className="flex items-center">
                  <div className="w-2 h-2 rounded-full mr-2" style={{ background: '#ff6b35' }}></div>
                  Disconnected
                </div>
              )}
            </Badge>
          </CardTitle>
          <CardDescription style={{ color: '#94a3b8' }}>
            Enterprise-grade email security monitoring and real-time threat protection
          </CardDescription>
        </CardHeader>
        <CardContent>
          {emailConnection.status === 'disconnected' ? (
            <div className="space-y-8">
              {/* Welcome Section */}
              <div className="text-center py-8">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center" style={{
                    background: 'rgba(0, 212, 255, 0.1)',
                    border: '2px solid rgba(0, 212, 255, 0.2)'
                  }}>
                  <Mail className="h-10 w-10" style={{ color: '#00d4ff' }} />
                </div>
                <h3 className="text-2xl font-bold mb-3" style={{ color: '#ffffff' }}>Protect Your Email</h3>
                <p className="text-lg mb-2" style={{ color: '#e2e8f0' }}>Connect your email account for real-time threat protection</p>
                <p className="text-sm" style={{ color: '#94a3b8' }}>Choose your email provider below to get started</p>
              </div>
              
              {/* Step-by-step Guide */}
              <div className="mb-8 p-6 rounded-xl" style={{
                  background: 'rgba(0, 255, 136, 0.05)',
                  border: '1px solid rgba(0, 255, 136, 0.2)'
                }}>
                <h4 className="text-lg font-semibold mb-4 flex items-center" style={{ color: '#00ff88' }}>
                  <CheckCircle className="h-5 w-5 mr-2" />
                  How Email Protection Works
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{
                        background: 'rgba(0, 212, 255, 0.2)'
                      }}>
                      <Lock className="h-6 w-6" style={{ color: '#00d4ff' }} />
                    </div>
                    <h5 className="font-medium mb-2" style={{ color: '#ffffff' }}>1. Secure Connection</h5>
                    <p className="text-sm" style={{ color: '#94a3b8' }}>We establish an encrypted connection to your email</p>
                  </div>
                  <div className="text-center p-4">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{
                        background: 'rgba(139, 92, 246, 0.2)'
                      }}>
                      <Shield className="h-6 w-6" style={{ color: '#8b5cf6' }} />
                    </div>
                    <h5 className="font-medium mb-2" style={{ color: '#ffffff' }}>2. Real-time Scanning</h5>
                    <p className="text-sm" style={{ color: '#94a3b8' }}>All emails and attachments are scanned for threats</p>
                  </div>
                  <div className="text-center p-4">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{
                        background: 'rgba(0, 255, 136, 0.2)'
                      }}>
                      <AlertTriangle className="h-6 w-6" style={{ color: '#00ff88' }} />
                    </div>
                    <h5 className="font-medium mb-2" style={{ color: '#ffffff' }}>3. Instant Protection</h5>
                    <p className="text-sm" style={{ color: '#94a3b8' }}>Threats are blocked automatically before reaching you</p>
                  </div>
                </div>
              </div>
                
              {/* Provider Selection */}
              <div>
                <h4 className="text-xl font-semibold mb-6 text-center" style={{ color: '#ffffff' }}>Choose Your Email Provider</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {emailProviders.map((provider) => {
                    const IconComponent = provider.icon;
                    return (
                      <div key={provider.value} className="group cursor-pointer transition-all duration-300" 
                        onClick={() => handleConnectEmail(provider)}
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          backdropFilter: 'blur(20px)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '1rem',
                          padding: '1.5rem'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-5px)';
                          e.currentTarget.style.boxShadow = '0 0 40px rgba(0, 212, 255, 0.3)';
                          e.currentTarget.style.borderColor = '#00d4ff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                        }}
                      >
                        <div className="flex items-start space-x-4">
                          <div className="flex-shrink-0">
                            <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{
                                background: 'rgba(0, 212, 255, 0.1)',
                                border: '1px solid rgba(0, 212, 255, 0.2)'
                              }}>
                              <IconComponent className="h-8 w-8" style={{ color: '#00d4ff' }} />
                            </div>
                          </div>
                          <div className="flex-grow min-w-0">
                            <h5 className="text-lg font-semibold mb-2" style={{ color: '#ffffff' }}>
                              {provider.name}
                            </h5>
                            <p className="text-sm mb-3" style={{ color: '#94a3b8' }}>
                              {provider.description}
                            </p>
                            <div className="space-y-1">
                              {provider.setupSteps.map((step, index) => (
                                <div key={index} className="flex items-center text-xs" style={{ color: '#e2e8f0' }}>
                                  <div className="w-1.5 h-1.5 rounded-full mr-2" style={{ background: '#00ff88' }}></div>
                                  {step}
                                </div>
                              ))}
                            </div>
                            <div className="flex space-x-2 mt-4">
                              <Button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowSetupGuide(provider.name);
                                }}
                                variant="outline"
                                className="flex-1 transition-all duration-200"
                                style={{
                                  border: '1px solid rgba(0, 212, 255, 0.3)',
                                  color: '#00d4ff',
                                  background: 'rgba(0, 212, 255, 0.05)'
                                }}
                              >
                                <FileText className="h-4 w-4 mr-2" />
                                Setup Guide
                              </Button>
                              <Button 
                                className="flex-1 transition-all duration-200" 
                                disabled={emailConnection.status === 'connecting'}
                                style={{
                                  background: 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)',
                                  color: '#ffffff',
                                  border: 'none'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.boxShadow = '0 0 20px rgba(0, 212, 255, 0.4)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.boxShadow = 'none';
                              }}
                            >
                              <Lock className="h-4 w-4 mr-2" />
                              Connect Securely
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                  })}
                </div>
              </div>

              {/* Security Features - Privacy & Compliance */}
              <div className="mt-8 p-6 rounded-xl" style={{
                  background: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                <h4 className="text-lg font-semibold mb-4" style={{ color: '#ffffff' }}>🔒 Your Privacy & Security</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center" style={{ color: '#94a3b8' }}>
                    <CheckCircle className="h-4 w-4 mr-2" style={{ color: '#00ff88' }} />
                    End-to-end encryption for all connections
                  </div>
                  <div className="flex items-center" style={{ color: '#94a3b8' }}>
                    <CheckCircle className="h-4 w-4 mr-2" style={{ color: '#00ff88' }} />
                    No email content stored on our servers
                  </div>
                  <div className="flex items-center" style={{ color: '#94a3b8' }}>
                    <CheckCircle className="h-4 w-4 mr-2" style={{ color: '#00ff88' }} />
                    GDPR & SOC2 compliant infrastructure
                  </div>
                  <div className="flex items-center" style={{ color: '#94a3b8' }}>
                    <CheckCircle className="h-4 w-4 mr-2" style={{ color: '#00ff88' }} />
                    Revoke access anytime from settings
                  </div>
                </div>
              </div>
            </div>
          ) : emailConnection.status === 'error' ? (
            <div className="text-center py-12">
              <div className="relative w-24 h-24 mx-auto mb-6">
                <div className="w-full h-full rounded-full flex items-center justify-center" style={{
                    background: 'rgba(255, 107, 53, 0.1)',
                    border: '2px solid rgba(255, 107, 53, 0.3)'
                  }}>
                  <AlertTriangle className="h-10 w-10" style={{ color: '#ff6b35' }} />
                </div>
              </div>
              <h3 className="text-2xl font-semibold mb-3" style={{ color: '#ffffff' }}>Connection Failed</h3>
              <p className="text-lg mb-6" style={{ color: '#e2e8f0' }}>{emailConnection.error}</p>
              
              <div className="max-w-md mx-auto space-y-4">
                <div className="p-4 rounded-lg" style={{
                    background: 'rgba(255, 107, 53, 0.1)',
                    border: '1px solid rgba(255, 107, 53, 0.3)'
                  }}>
                  <h4 className="font-semibold mb-2" style={{ color: '#ff6b35' }}>Troubleshooting Tips:</h4>
                  <ul className="text-sm space-y-1" style={{ color: '#e2e8f0' }}>
                    <li>• Verify your email and password are correct</li>
                    <li>• Check if 2-factor authentication is enabled</li>
                    <li>• Ensure your internet connection is stable</li>
                    <li>• Try using an app-specific password</li>
                  </ul>
                </div>
                
                <div className="flex space-x-4">
                  <Button 
                    onClick={() => setEmailConnection(prev => ({ ...prev, status: 'disconnected', error: null }))}
                    className="flex-1 transition-all duration-200" 
                    style={{
                      background: 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)',
                      color: '#ffffff',
                      border: 'none'
                    }}
                  >
                    Try Again
                  </Button>
                  <Button
                    onClick={() => window.open('mailto:team@cyberguardglobal.com?subject=Email Connection Issue', '_blank')}
                    variant="outline"
                    className="transition-all duration-200"
                    style={{
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      color: '#e2e8f0',
                      background: 'rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    Get Help
                  </Button>
                </div>
              </div>
            </div>
          ) : emailConnection.status === 'connecting' ? (
            <div className="text-center py-12">
              <div className="relative w-24 h-24 mx-auto mb-6">
                <div className="absolute inset-0 rounded-full border-4 border-transparent" style={{
                    borderTop: '4px solid #00d4ff',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                <div className="w-full h-full rounded-full flex items-center justify-center" style={{
                    background: 'rgba(0, 212, 255, 0.1)'
                  }}>
                  <Lock className="h-10 w-10" style={{ color: '#00d4ff' }} />
                </div>
              </div>
              <h3 className="text-2xl font-semibold mb-3" style={{ color: '#ffffff' }}>Establishing Secure Connection</h3>
              <p className="text-lg mb-6" style={{ color: '#e2e8f0' }}>Connecting to {emailConnection.provider} with enterprise-grade encryption</p>
              
              <div className="max-w-md mx-auto space-y-3">
                <div className="flex items-center justify-between text-sm" style={{ color: '#94a3b8' }}>
                  <span>Authenticating credentials</span>
                  <CheckCircle className="h-4 w-4" style={{ color: '#00ff88' }} />
                </div>
                <div className="flex items-center justify-between text-sm" style={{ color: '#94a3b8' }}>
                  <span>Establishing secure tunnel</span>
                  <RefreshCw className="h-4 w-4 animate-spin" style={{ color: '#00d4ff' }} />
                </div>
                <div className="flex items-center justify-between text-sm opacity-50" style={{ color: '#94a3b8' }}>
                  <span>Initializing threat detection</span>
                  <div className="w-4 h-4 rounded-full border" style={{ borderColor: '#94a3b8' }}></div>
                </div>
              </div>
              
              <div className="mt-8">
                <Progress value={65} className="max-w-sm mx-auto" style={{
                    background: 'rgba(255, 255, 255, 0.1)'
                  }} />
                <p className="text-xs mt-2" style={{ color: '#94a3b8' }}>This may take up to 30 seconds...</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Connection Success Banner */}
              <div className="p-6 rounded-xl transition-all duration-300" style={{
                  background: 'rgba(0, 255, 136, 0.1)',
                  border: '1px solid rgba(0, 255, 136, 0.3)',
                  boxShadow: '0 0 20px rgba(0, 255, 136, 0.1)'
                }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{
                        background: 'rgba(0, 255, 136, 0.2)'
                      }}>
                      <CheckCircle className="h-6 w-6" style={{ color: '#00ff88' }} />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold" style={{ color: '#ffffff' }}>Email Protection Active</h4>
                      <p className="text-sm" style={{ color: '#e2e8f0' }}>Connected to {emailConnection.provider}</p>
                      <p className="text-xs" style={{ color: '#94a3b8' }}>{emailConnection.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="flex items-center space-x-2 mb-1">
                        <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#00ff88' }}></div>
                        <span className="text-sm font-medium" style={{ color: '#00ff88' }}>Live Monitoring</span>
                      </div>
                      <p className="text-xs" style={{ color: '#94a3b8' }}>Last sync: {formatLocalTime(emailConnection.lastSync)}</p>
                    </div>
                    <Button
                      onClick={debugMonitoring}
                      variant="outline"
                      size="sm"
                      className="transition-all duration-200"
                      style={{
                        border: '1px solid rgba(0, 212, 255, 0.3)',
                        color: '#00d4ff',
                        background: 'rgba(0, 212, 255, 0.1)'
                      }}
                    >
                      Debug
                    </Button>
                    <Button
                      onClick={handleDisconnect}
                      variant="outline"
                      size="sm"
                      className="transition-all duration-200"
                      style={{
                        border: '1px solid rgba(255, 107, 53, 0.3)',
                        color: '#ff6b35',
                        background: 'rgba(255, 107, 53, 0.1)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 107, 53, 0.2)';
                        e.target.style.boxShadow = '0 0 10px rgba(255, 107, 53, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 107, 53, 0.1)';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Disconnect
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Quick Stats Overview */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="text-center p-4 rounded-xl transition-all duration-300" style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(0, 255, 136, 0.2)'
                  }}>
                  <div className="text-2xl font-bold mb-1" style={{ color: '#00ff88' }}>{emailStats.emailsScanned}</div>
                  <div className="text-sm" style={{ color: '#94a3b8' }}>Emails Scanned</div>
                </div>
                <div className="text-center p-4 rounded-xl transition-all duration-300" style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 107, 53, 0.2)'
                  }}>
                  <div className="text-2xl font-bold mb-1" style={{ color: '#ff6b35' }}>{emailStats.threatsBlocked}</div>
                  <div className="text-sm" style={{ color: '#94a3b8' }}>Threats Blocked</div>
                </div>
                <div className="text-center p-4 rounded-xl transition-all duration-300" style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(139, 92, 246, 0.2)'
                  }}>
                  <div className="text-2xl font-bold mb-1" style={{ color: '#8b5cf6' }}>{emailStats.attachmentsChecked}</div>
                  <div className="text-sm" style={{ color: '#94a3b8' }}>Attachments</div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Protection Dashboard */}
      {emailConnection.status === 'connected' && (
        <Tabs defaultValue="monitoring" className="space-y-6">
          <TabsList className="p-1 transition-all duration-300" style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '1rem'
            }}>
            <TabsTrigger value="monitoring" className="transition-all duration-200 data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-300 text-gray-400">
              <Monitor className="h-4 w-4 mr-2" />
              Real-time Monitoring
            </TabsTrigger>
            <TabsTrigger value="analytics" className="transition-all duration-200 data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-300 text-gray-400">
              <BarChart3 className="h-4 w-4 mr-2" />
              Security Analytics
            </TabsTrigger>
            <TabsTrigger value="quarantine" className="transition-all duration-200 data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-300 text-gray-400">
              <Shield className="h-4 w-4 mr-2" />
              Quarantine
            </TabsTrigger>
            <TabsTrigger value="spam-blocker" className="transition-all duration-200 data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-300 text-gray-400">
              <Filter className="h-4 w-4 mr-2" />
              Spam Blocker
            </TabsTrigger>
            <TabsTrigger value="settings" className="transition-all duration-200 data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-300 text-gray-400">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Real-time Monitoring Tab */}
          <TabsContent value="monitoring" className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
              <div className="text-center p-4 rounded-lg transition-all duration-300" style={{
                  background: 'rgba(0, 255, 136, 0.1)',
                  border: '1px solid rgba(0, 255, 136, 0.2)'
                }}>
                <div className="text-2xl font-bold" style={{ color: '#00ff88' }}>{emailStats.emailsScanned}</div>
                <div className="text-sm" style={{ color: '#94a3b8' }}>Emails Scanned</div>
              </div>
              <div className="text-center p-4 rounded-lg transition-all duration-300" style={{
                  background: 'rgba(255, 107, 53, 0.1)',
                  border: '1px solid rgba(255, 107, 53, 0.2)'
                }}>
                <div className="text-2xl font-bold" style={{ color: '#ff6b35' }}>{emailStats.threatsBlocked}</div>
                <div className="text-sm" style={{ color: '#94a3b8' }}>Threats Blocked</div>
              </div>
              <div className="text-center p-4 rounded-lg transition-all duration-300" style={{
                  background: 'rgba(139, 92, 246, 0.1)',
                  border: '1px solid rgba(139, 92, 246, 0.2)'
                }}>
                <div className="text-2xl font-bold" style={{ color: '#8b5cf6' }}>{emailStats.attachmentsChecked}</div>
                <div className="text-sm" style={{ color: '#94a3b8' }}>Attachments</div>
              </div>
              <div className="text-center p-4 rounded-lg transition-all duration-300" style={{
                  background: 'rgba(249, 115, 22, 0.1)',
                  border: '1px solid rgba(249, 115, 22, 0.2)'
                }}>
                <div className="text-2xl font-bold" style={{ color: '#f97316' }}>{emailStats.phishingDetected}</div>
                <div className="text-sm" style={{ color: '#94a3b8' }}>Phishing</div>
              </div>
              <div className="text-center p-4 rounded-lg transition-all duration-300" style={{
                  background: 'rgba(0, 212, 255, 0.1)',
                  border: '1px solid rgba(0, 212, 255, 0.2)'
                }}>
                <div className="text-2xl font-bold" style={{ color: '#00d4ff' }}>{emailStats.spamBlocked}</div>
                <div className="text-sm" style={{ color: '#94a3b8' }}>Spam Blocked</div>
              </div>
              <div className="text-center p-4 rounded-lg transition-all duration-300" style={{
                  background: 'rgba(234, 179, 8, 0.1)',
                  border: '1px solid rgba(234, 179, 8, 0.2)'
                }}>
                <div className="text-2xl font-bold" style={{ color: '#eab308' }}>{emailStats.malwareQuarantined}</div>
                <div className="text-sm" style={{ color: '#94a3b8' }}>Quarantined</div>
              </div>
            </div>

            {/* Real-time Alerts */}
            <Card className="transition-all duration-300" style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '1rem'
              }}>
              <CardHeader>
                <CardTitle className="flex items-center" style={{ color: '#ffffff' }}>
                  <Activity className="h-5 w-5 mr-2" style={{ color: '#00d4ff' }} />
                  Live Security Alerts
                  <Badge className="ml-3 border-0" style={{
                      background: 'rgba(0, 255, 136, 0.2)',
                      color: '#00ff88'
                    }}>
                    <div className="w-2 h-2 rounded-full mr-2 animate-pulse" style={{ background: '#00ff88' }}></div>
                    Live
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {realtimeAlerts.length === 0 ? (
                    <div className="text-center py-8">
                      <CheckCircle className="h-8 w-8 mx-auto mb-2" style={{ color: '#00ff88' }} />
                      <p style={{ color: '#94a3b8' }}>No security alerts at this time</p>
                    </div>
                  ) : (
                    realtimeAlerts.map((alert) => (
                      <div key={alert.id} className="flex items-center justify-between p-3 rounded-lg transition-all duration-200" style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                        <div className="flex items-center space-x-3">
                          {getAlertIcon(alert.type)}
                          <div>
                            <p className="text-sm font-medium" style={{ color: '#ffffff' }}>{alert.message}</p>
                            <p className="text-xs" style={{ color: '#94a3b8' }}>{formatLocalTime(alert.timestamp)}</p>
                          </div>
                        </div>
                        <Badge className={getSeverityColor(alert.severity)} style={{
                            background: alert.severity === 'high' ? 'rgba(255, 107, 53, 0.2)' :
                                       alert.severity === 'medium' ? 'rgba(234, 179, 8, 0.2)' :
                                       'rgba(0, 255, 136, 0.2)',
                            color: alert.severity === 'high' ? '#ff6b35' :
                                  alert.severity === 'medium' ? '#eab308' :
                                  '#00ff88',
                            border: 'none'
                          }}>
                          {alert.severity.toUpperCase()}
                        </Badge>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="transition-all duration-300" style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '1rem'
                }}>
                <CardHeader>
                  <CardTitle className="flex items-center" style={{ color: '#ffffff' }}>
                    <PieChart className="h-5 w-5 mr-2" style={{ color: '#00d4ff' }} />
                    Threat Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { type: 'Spam', count: emailStats.spamBlocked, color: '#00d4ff' },
                      { type: 'Phishing', count: emailStats.phishingDetected, color: '#f97316' },
                      { type: 'Malware', count: emailStats.malwareQuarantined, color: '#ff6b35' },
                      { type: 'Other', count: 5, color: '#8b5cf6' }
                    ].map((threat, index) => {
                      const total = emailStats.spamBlocked + emailStats.phishingDetected + emailStats.malwareQuarantined + 5;
                      const percentage = ((threat.count / total) * 100).toFixed(1);
                      
                      return (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: threat.color }}></div>
                            <span className="text-sm" style={{ color: '#ffffff' }}>{threat.type}</span>
                          </div>
                          <div className="text-right">
                            <span className="font-medium" style={{ color: '#ffffff' }}>{threat.count}</span>
                            <span className="text-sm ml-2" style={{ color: '#94a3b8' }}>({percentage}%)</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              <Card className="transition-all duration-300" style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '1rem'
                }}>
                <CardHeader>
                  <CardTitle className="flex items-center" style={{ color: '#ffffff' }}>
                    <TrendingUp className="h-5 w-5 mr-2" style={{ color: '#00ff88' }} />
                    Protection Trends
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm" style={{ color: '#94a3b8' }}>Detection Rate</span>
                      <span className="font-bold" style={{ color: '#00ff88' }}>98.7%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm" style={{ color: '#94a3b8' }}>False Positives</span>
                      <span className="font-bold" style={{ color: '#eab308' }}>0.2%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm" style={{ color: '#94a3b8' }}>Avg Response Time</span>
                      <span className="font-bold" style={{ color: '#00d4ff' }}>0.3ms</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm" style={{ color: '#94a3b8' }}>Uptime</span>
                      <span className="font-bold" style={{ color: '#00ff88' }}>99.9%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Quarantine Tab */}
          <TabsContent value="quarantine" className="space-y-6">
            <Card className="transition-all duration-300" style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '1rem'
              }}>
              <CardHeader>
                <CardTitle className="flex items-center" style={{ color: '#ffffff' }}>
                  <Shield className="h-5 w-5 mr-2" style={{ color: '#ff6b35' }} />
                  Quarantined Items
                </CardTitle>
                <CardDescription style={{ color: '#94a3b8' }}>
                  Suspicious emails and attachments awaiting review
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingQuarantine ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 mx-auto mb-4" style={{ borderColor: '#00d4ff' }}></div>
                    <p className="text-sm" style={{ color: '#94a3b8' }}>Loading quarantine items...</p>
                  </div>
                ) : emailConnection.status === 'connected' && quarantineItems.length === 0 ? (
                  <div className="text-center py-12">
                    <Shield className="h-16 w-16 mx-auto mb-4" style={{ color: '#00ff88', opacity: 0.5 }} />
                    <h3 className="text-lg font-semibold mb-2" style={{ color: '#ffffff' }}>No Threats Detected</h3>
                    <p className="text-sm" style={{ color: '#94a3b8' }}>
                      Your email is being monitored. Quarantined items will appear here when threats are detected.
                    </p>
                    <div className="mt-4 p-4 rounded-lg" style={{
                        background: 'rgba(0, 255, 136, 0.1)',
                        border: '1px solid rgba(0, 255, 136, 0.2)'
                      }}>
                      <p className="text-xs" style={{ color: '#00ff88' }}>
                        ✓ {emailStats.emailsScanned} emails scanned • Real-time protection active
                      </p>
                    </div>
                  </div>
                ) : emailConnection.status === 'connected' && quarantineItems.length > 0 ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4 p-4 rounded-lg" style={{
                        background: 'rgba(255, 107, 53, 0.1)',
                        border: '1px solid rgba(255, 107, 53, 0.3)'
                      }}>
                      <div>
                        <p className="text-lg font-bold" style={{ color: '#ff6b35' }}>
                          {quarantineItems.length} Threat{quarantineItems.length !== 1 ? 's' : ''} Quarantined
                        </p>
                        <p className="text-xs" style={{ color: '#94a3b8' }}>
                          Review and take action on suspicious emails
                        </p>
                      </div>
                      <Button
                        onClick={fetchQuarantineItems}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2"
                        style={{
                          background: 'rgba(0, 212, 255, 0.1)',
                          border: '1px solid rgba(0, 212, 255, 0.3)',
                          color: '#00d4ff'
                        }}
                      >
                        <RefreshCw className="h-4 w-4" />
                        Refresh
                      </Button>
                    </div>
                    
                    {quarantineItems.map((item, index) => (
                      <div key={index} className="p-4 rounded-lg transition-all duration-300 hover:shadow-lg" style={{
                          background: 'rgba(255, 255, 255, 0.03)',
                          border: '1px solid rgba(255, 107, 53, 0.3)'
                        }}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <AlertTriangle className="h-4 w-4" style={{ color: '#ff6b35' }} />
                              <h4 className="font-semibold" style={{ color: '#ffffff' }}>
                                {item.email_subject || 'No Subject'}
                              </h4>
                            </div>
                            <p className="text-sm mb-1" style={{ color: '#94a3b8' }}>
                              <strong>From:</strong> {item.email_from}
                            </p>
                            <p className="text-xs" style={{ color: '#64748b' }}>
                              <strong>Date:</strong> {item.email_date} • <strong>Quarantined:</strong> {formatLocalTime(item.timestamp)}
                            </p>
                          </div>
                        </div>
                        
                        {/* Threat Details */}
                        <div className="mb-3 p-3 rounded-lg" style={{
                            background: 'rgba(255, 107, 53, 0.1)',
                            border: '1px solid rgba(255, 107, 53, 0.2)'
                          }}>
                          <p className="text-xs font-semibold mb-2" style={{ color: '#ff6b35' }}>Detected Threats:</p>
                          <div className="space-y-1">
                            {item.threats.map((threat, i) => (
                              <div key={i} className="text-xs" style={{ color: '#e2e8f0' }}>
                                • <strong>{threat.type}:</strong> {threat.description}
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleTrustSender(item)}
                            size="sm"
                            className="flex items-center gap-2"
                            style={{
                              background: 'rgba(0, 212, 255, 0.1)',
                              border: '1px solid rgba(0, 212, 255, 0.3)',
                              color: '#00d4ff'
                            }}
                          >
                            <CheckCircle className="h-4 w-4" />
                            Trust Sender
                          </Button>
                          <Button
                            onClick={() => handleQuarantineAction(item, 'released')}
                            size="sm"
                            className="flex items-center gap-2"
                            style={{
                              background: 'rgba(0, 255, 136, 0.1)',
                              border: '1px solid rgba(0, 255, 136, 0.3)',
                              color: '#00ff88'
                            }}
                          >
                            <CheckCircle className="h-4 w-4" />
                            Release
                          </Button>
                          <Button
                            onClick={() => handleQuarantineAction(item, 'deleted')}
                            size="sm"
                            className="flex items-center gap-2"
                            style={{
                              background: 'rgba(255, 107, 53, 0.1)',
                              border: '1px solid rgba(255, 107, 53, 0.3)',
                              color: '#ff6b35'
                            }}
                          >
                            <XCircle className="h-4 w-4" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Lock className="h-16 w-16 mx-auto mb-4" style={{ color: '#94a3b8', opacity: 0.3 }} />
                    <h3 className="text-lg font-semibold mb-2" style={{ color: '#ffffff' }}>Email Not Connected</h3>
                    <p className="text-sm" style={{ color: '#94a3b8' }}>
                      Connect your email to start monitoring for threats
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Spam Blocker Tab */}
          <TabsContent value="spam-blocker" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="transition-all duration-300" style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '1rem'
                }}>
                <CardHeader>
                  <CardTitle className="flex items-center" style={{ color: '#ffffff' }}>
                    <Filter className="h-5 w-5 mr-2" style={{ color: '#00d4ff' }} />
                    Spam Filter Settings
                  </CardTitle>
                  <CardDescription style={{ color: '#94a3b8' }}>
                    Configure intelligent spam detection and blocking rules
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    { label: 'Aggressive Spam Filtering', key: 'aggressiveFiltering', locked: false, description: 'Block obvious spam messages' },
                    { label: 'Bayesian Learning', key: 'bayesianLearning', locked: false, description: 'AI-powered spam detection' },
                    { label: 'Sender Reputation Check', key: 'senderReputation', locked: false, description: 'Verify sender authenticity' },
                    { label: 'Content Analysis', key: 'contentAnalysis', locked: tierInfo.name === 'Free Trial', description: 'Deep content scanning' },
                    { label: 'Language Detection', key: 'languageDetection', locked: false, description: 'Filter foreign language spam' },
                    { label: 'Attachment Scanning', key: 'attachmentScanning', locked: tierInfo.name !== 'Enterprise Premium Guard', description: 'Scan attachments for spam indicators' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 rounded-lg" style={{
                        background: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                      <div className="flex-1">
                        <span className="font-medium" style={{ color: '#e2e8f0' }}>
                          {item.label}
                          {item.locked && <span className="ml-2 text-xs" style={{ color: '#94a3b8' }}>🔒 Upgrade required</span>}
                        </span>
                        <p className="text-xs" style={{ color: '#94a3b8' }}>{item.description}</p>
                      </div>
                      <div 
                        className={`w-12 h-6 rounded-full transition-colors ${item.locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        style={{
                          backgroundColor: spamFilterSettings[item.key] && !item.locked ? '#00ff88' : '#64748b'
                        }}
                        onClick={() => {
                          if (!item.locked) {
                            setSpamFilterSettings(prev => ({ ...prev, [item.key]: !prev[item.key] }));
                            console.log(`🔄 Toggled ${item.label}: ${!spamFilterSettings[item.key]}`);
                          }
                        }}
                      >
                        <div className={`w-5 h-5 rounded-full transition-transform ${
                          spamFilterSettings[item.key] && !item.locked ? 'translate-x-6' : 'translate-x-1'
                        } mt-0.5`} style={{ backgroundColor: '#ffffff' }}></div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="transition-all duration-300" style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '1rem'
                }}>
                <CardHeader>
                  <CardTitle className="flex items-center" style={{ color: '#ffffff' }}>
                    <CheckCircle className="h-5 w-5 mr-2" style={{ color: '#00ff88' }} />
                    Spam Statistics Today
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="text-center p-4 rounded-lg" style={{
                        background: 'rgba(0, 255, 136, 0.1)',
                        border: '1px solid rgba(0, 255, 136, 0.2)'
                      }}>
                      <div className="text-3xl font-bold" style={{ color: '#00ff88' }}>{emailStats.spamBlockedToday}</div>
                      <div className="text-sm" style={{ color: '#94a3b8' }}>Spam Emails Blocked</div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-3 rounded-lg" style={{
                          background: 'rgba(255, 255, 255, 0.03)',
                          border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                        <div className="text-xl font-bold" style={{ color: '#ff6b35' }}>{emailStats.suspiciousEmails}</div>
                        <div className="text-xs" style={{ color: '#94a3b8' }}>Suspicious</div>
                      </div>
                      <div className="text-center p-3 rounded-lg" style={{
                          background: 'rgba(255, 255, 255, 0.03)',
                          border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                        <div className="text-xl font-bold" style={{ color: '#00d4ff' }}>{emailStats.whitelistedSenders}</div>
                        <div className="text-xs" style={{ color: '#94a3b8' }}>Whitelisted</div>
                      </div>
                    </div>

                    <div className="p-4 rounded-lg" style={{
                        background: 'rgba(0, 212, 255, 0.1)',
                        border: '1px solid rgba(0, 212, 255, 0.2)'
                      }}>
                      <h4 className="font-semibold mb-2" style={{ color: '#00d4ff' }}>Quick Actions</h4>
                      <div className="space-y-2">
                        <Button className="w-full justify-start" variant="outline" size="sm" style={{
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            color: '#e2e8f0',
                            background: 'rgba(255, 255, 255, 0.05)'
                          }}>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Sender to Whitelist
                        </Button>
                        <Button className="w-full justify-start" variant="outline" size="sm" style={{
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            color: '#e2e8f0',
                            background: 'rgba(255, 255, 255, 0.05)'
                          }}>
                          <X className="h-4 w-4 mr-2" />
                          Block Specific Domain
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="transition-all duration-300" style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '1rem'
                }}>
                <CardHeader>
                  <CardTitle style={{ color: '#ffffff' }}>Protection Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    { label: 'Real-time Scanning', key: 'realtimeScanning', locked: false },
                    { label: 'Attachment Analysis', key: 'attachmentAnalysis', locked: false },
                    { label: 'Phishing Detection', key: 'phishingDetection', locked: false },
                    { label: 'Spam Filtering', key: 'spamFiltering', locked: false },
                    { label: 'Content Analysis', key: 'contentAnalysis', locked: tierInfo.name === 'Free Trial' },
                    { label: 'Advanced Heuristics', key: 'advancedHeuristics', locked: tierInfo.name !== 'Enterprise Premium Guard' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span style={{ color: '#e2e8f0' }}>
                        {item.label}
                        {item.locked && <span className="ml-2 text-xs" style={{ color: '#94a3b8' }}>🔒 Upgrade required</span>}
                      </span>
                      <div 
                        className={`w-12 h-6 rounded-full transition-colors ${item.locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        style={{
                          backgroundColor: protectionSettings[item.key] && !item.locked ? '#00ff88' : '#64748b'
                        }}
                        onClick={() => !item.locked && setProtectionSettings(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                      >
                        <div className={`w-5 h-5 rounded-full transition-transform ${
                          protectionSettings[item.key] && !item.locked ? 'translate-x-6' : 'translate-x-1'
                        } mt-0.5`} style={{ backgroundColor: '#ffffff' }}></div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="transition-all duration-300" style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '1rem'
                }}>
                <CardHeader>
                  <CardTitle style={{ color: '#ffffff' }}>Notification Settings</CardTitle>
                  <CardDescription style={{ color: '#94a3b8' }}>
                    Configure how you want to receive security alerts
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Contact Information */}
                  <div className="space-y-4 p-4 rounded-lg" style={{
                      background: 'rgba(0, 212, 255, 0.05)',
                      border: '1px solid rgba(0, 212, 255, 0.2)'
                    }}>
                    <h4 className="font-semibold" style={{ color: '#00d4ff' }}>Contact Information</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium block mb-2" style={{ color: '#e2e8f0' }}>
                          Email Address for Alerts
                        </label>
                        <Input
                          type="email"
                          placeholder="your.email@example.com"
                          value={contactInfo.email_address}
                          onChange={(e) => setContactInfo(prev => ({ ...prev, email_address: e.target.value }))}
                          style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            color: '#ffffff'
                          }}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium block mb-2" style={{ color: '#e2e8f0' }}>
                          Phone Number for SMS (Optional)
                        </label>
                        <Input
                          type="tel"
                          placeholder="+1234567890"
                          value={contactInfo.phone_number}
                          onChange={(e) => setContactInfo(prev => ({ ...prev, phone_number: e.target.value }))}
                          style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            color: '#ffffff'
                          }}
                        />
                        <p className="text-xs mt-1" style={{ color: '#94a3b8' }}>Include country code (e.g., +1 for US)</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Notification Toggles */}
                  <div className="space-y-3">
                    {[
                      { label: 'Email Alerts', key: 'emailAlerts', description: 'Receive threat alerts via email' },
                      { label: 'SMS Notifications', key: 'smsNotifications', description: 'Get text messages for critical threats' },
                      { label: 'Desktop Notifications', key: 'desktopNotifications', description: 'Browser popup notifications' },
                      { label: 'Weekly Reports', key: 'weeklyReports', description: 'Weekly security summary via email' },
                      { label: 'Emergency Alerts', key: 'emergencyAlerts', description: 'High-priority threat notifications' }
                    ].map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 rounded-lg" style={{
                          background: 'rgba(255, 255, 255, 0.03)',
                          border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                        <div className="flex-1">
                          <span className="font-medium" style={{ color: '#e2e8f0' }}>{item.label}</span>
                          <p className="text-xs mt-1" style={{ color: '#94a3b8' }}>{item.description}</p>
                        </div>
                        <div 
                          className="w-12 h-6 rounded-full transition-colors cursor-pointer"
                          style={{
                            backgroundColor: notificationSettings[item.key] ? '#00d4ff' : '#64748b'
                          }}
                          onClick={() => setNotificationSettings(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                        >
                          <div className={`w-5 h-5 rounded-full transition-transform ${
                            notificationSettings[item.key] ? 'translate-x-6' : 'translate-x-1'
                          } mt-0.5`} style={{ backgroundColor: '#ffffff' }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="space-y-2 pt-4 border-t" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                    <Button
                      onClick={saveNotificationSettings}
                      className="w-full"
                      style={{
                        background: 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)',
                        color: '#ffffff',
                        border: 'none'
                      }}
                    >
                      Save Notification Settings
                    </Button>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => sendTestNotification('email')}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        style={{
                          border: '1px solid rgba(0, 212, 255, 0.3)',
                          color: '#00d4ff',
                          background: 'rgba(0, 212, 255, 0.1)'
                        }}
                      >
                        Test Email
                      </Button>
                      <Button
                        onClick={() => sendTestNotification('sms')}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        style={{
                          border: '1px solid rgba(0, 212, 255, 0.3)',
                          color: '#00d4ff',
                          background: 'rgba(0, 212, 255, 0.1)'
                        }}
                      >
                        Test SMS
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Whitelist Management */}
            <Card className="transition-all duration-300" style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '1rem'
              }}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between" style={{ color: '#ffffff' }}>
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2" style={{ color: '#00d4ff' }} />
                    Trusted Senders
                  </div>
                  <Button
                    onClick={fetchWhitelist}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                    style={{
                      background: 'rgba(0, 212, 255, 0.1)',
                      border: '1px solid rgba(0, 212, 255, 0.3)',
                      color: '#00d4ff'
                    }}
                  >
                    <RefreshCw className="h-4 w-4" />
                    Refresh
                  </Button>
                </CardTitle>
                <CardDescription style={{ color: '#94a3b8' }}>
                  Emails from these senders will not be flagged as threats
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingWhitelist ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-t-2 mx-auto" style={{ borderColor: '#00d4ff' }}></div>
                  </div>
                ) : whitelistItems.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="h-12 w-12 mx-auto mb-3" style={{ color: '#00d4ff', opacity: 0.5 }} />
                    <p className="text-sm" style={{ color: '#94a3b8' }}>
                      No trusted senders yet. Click "Trust Sender" in the Quarantine tab to add.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {whitelistItems.map((item, index) => (
                      <div key={index} className="p-3 rounded-lg flex items-center justify-between transition-all duration-300 hover:shadow-lg" style={{
                          background: 'rgba(255, 255, 255, 0.03)',
                          border: '1px solid rgba(0, 212, 255, 0.2)'
                        }}>
                        <div className="flex-1">
                          <p className="font-medium" style={{ color: '#ffffff' }}>{item.email}</p>
                          <p className="text-xs" style={{ color: '#94a3b8' }}>
                            {item.reason} • Added {new Date(item.added_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button
                          onClick={() => handleRemoveFromWhitelist(item.email)}
                          size="sm"
                          variant="outline"
                          className="flex items-center gap-2"
                          style={{
                            background: 'rgba(255, 107, 53, 0.1)',
                            border: '1px solid rgba(255, 107, 53, 0.3)',
                            color: '#ff6b35'
                          }}
                        >
                          <XCircle className="h-4 w-4" />
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* App Password Help Panel */}
      <EmailAppPasswordHelp
        isOpen={showHelpPanel}
        onClose={() => setShowHelpPanel(false)}
      />
    </div>
  );
};

// Export without React.memo to allow state updates to trigger re-renders
export default EmailProtection;